// src/pages/ToDoPage.js
/**
 * Stand-alone "To Do" page.
 *
 * Renders the protocol task board — the same element shown on the Home page —
 * as the primary content of its own screen, keeping the card styling
 * consistent with the rest of the app.
 *
 * @returns {JSX.Element}
 */

import React, { memo, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { trackEvent } from "../utils/analytics/track";
import ProtocolTaskList from "../components/tasks/ProtocolTaskList";
import SkeletonBlock from "../components/common/SkeletonBlock";
import TaskDueCalendar, {
  taskBoardHasSchedulableTasks,
} from "../components/tasks/TaskDueCalendar";
import { getTaskBoardOccurrenceCompletionSummary } from "../utils/tasks/taskBoardSummary";

function getProgressPercent(completed, total) {
  if (!Number.isFinite(total) || total <= 0) {
    return 0;
  }

  return Math.max(0, Math.min(100, (completed / total) * 100));
}

function ToDoPage({ taskBoard, loadingModes, accountCreatedAt = null }) {
  const { t } = useTranslation("common");
  const [isCalendarExpanded, setIsCalendarExpanded] = useState(false);

  useEffect(() => {
    trackEvent("todo_view");
  }, []);

  const board = useMemo(() => {
    const baseBoard = {
      ...(taskBoard || {}),
      loading: loadingModes || taskBoard?.loading,
    };

    if (!accountCreatedAt) {
      return baseBoard;
    }

    return {
      ...baseBoard,
      contextMetadata: {
        ...(baseBoard.contextMetadata || {}),
        createdAt: accountCreatedAt,
      },
      scheduleAnchorAt: accountCreatedAt,
    };
  }, [accountCreatedAt, loadingModes, taskBoard]);
  const showCalendarToggle = useMemo(
    () => !board.loading && taskBoardHasSchedulableTasks(board),
    [board],
  );
  const completionSummary = useMemo(
    () => getTaskBoardOccurrenceCompletionSummary(board),
    [board],
  );
  const summaryLabel = t("taskList.page.summary", {
    completed: completionSummary.completed,
    total: completionSummary.total,
  });
  const showProgressSummary = completionSummary.total > 0;
  const progressPercent = getProgressPercent(
    completionSummary.completed,
    completionSummary.total,
  );

  if (board.loading) {
    // Shaped like the page that follows it: the calendar starts closed, so the
    // shimmer shows the header and its toggle rather than an expanded month the
    // user never asked for.
    return (
      <div className="w-full max-w-lg h-full min-h-fit px-4 pt-0 pb-4 flex flex-col items-center gap-4 overflow-y-scroll overflow-clip">
        <div className="w-full -mb-1 flex flex-col gap-2">
          <div className="flex w-full items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <h1 className="subtitle-text text-left">
                {t("taskList.page.title")}
              </h1>
            </div>

            <div className="flex items-center gap-2">
              <SkeletonBlock className="h-10 w-10 shrink-0 rounded-full" />
            </div>
          </div>

          <div className="w-full flex flex-col gap-2 px-1">
            <SkeletonBlock className="h-4 w-24 rounded-full" />
            <SkeletonBlock className="h-2 w-full rounded-full" />
          </div>
        </div>

        <ProtocolTaskList
          board={board}
          className="!bg-transparent !shadow-none !p-0 flex-1 min-h-0 "
          tasksClassName="flex-1 min-h-0 pr-1"
          groupedRows
          hideHeader
          emphasizeCounts
          splitCompleted
          outstandingSectionTitle={t("taskList.page.sections.outstanding")}
          completedSectionTitle={t("taskList.page.sections.completedToday")}
        />
      </div>
    );
  }

  return (
    <div className="w-full max-w-lg h-full min-h-fit px-4 pt-0 pb-4 flex flex-col items-center gap-4 overflow-y-scroll overflow-clip">
      <div className="w-full -mb-1 flex flex-col gap-2">
        <div className="flex w-full items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <h1 className="subtitle-text text-left">
              {t("taskList.page.title")}
            </h1>
          </div>

          {showCalendarToggle ? (
            <button
              type="button"
              className={[
                "mt-0.5 flex h-10 w-10 shrink-0 items-center rounded-full justify-center transition-colors",
                isCalendarExpanded
                  ? " bg-primary text-greyscale-1 shadow"
                  : " text-primary hover:bg-primary/5",
              ].join(" ")}
              aria-expanded={isCalendarExpanded}
              aria-controls="todo-page-calendar"
              aria-label={
                isCalendarExpanded
                  ? t("taskCalendar.hideCalendar")
                  : t("taskCalendar.showCalendar")
              }
              onClick={() => setIsCalendarExpanded((current) => !current)}
            >
              <span
                className="material-symbols-outlined text-[22px] leading-none"
                aria-hidden="true"
              >
                calendar_month
              </span>
            </button>
          ) : null}
        </div>

        {showProgressSummary ? (
          <div className="w-full flex flex-col gap-2 px-1">
            <p className="text-sm tabular-nums text-typography">
              {summaryLabel}
            </p>

            <div
              className="h-2 w-full overflow-hidden rounded-full bg-greyscale-1"
              role="progressbar"
              aria-label={t("taskList.page.progressLabel")}
              aria-valuemin={0}
              aria-valuemax={completionSummary.total}
              aria-valuenow={completionSummary.completed}
              aria-valuetext={summaryLabel}
            >
              <div
                className="h-full rounded-full bg-primary transition-[width] duration-300"
                style={{ width: `${progressPercent}%` }}
              />
            </div>
          </div>
        ) : null}
      </div>

      {showCalendarToggle && isCalendarExpanded ? (
        <div id="todo-page-calendar" className="w-full">
          <TaskDueCalendar board={board} />
        </div>
      ) : null}

      <ProtocolTaskList
        board={board}
        className="!bg-transparent !shadow-none !p-0 flex-1 min-h-0 "
        tasksClassName="flex-1 min-h-0 pr-1"
        groupedRows
        hideHeader
        emphasizeCounts
        splitCompleted
        outstandingSectionTitle={t("taskList.page.sections.outstanding")}
        completedSectionTitle={t("taskList.page.sections.completedToday")}
      />
    </div>
  );
}

export default memo(ToDoPage);
